import { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ContentPasteGoOutlinedIcon from "@mui/icons-material/ContentPasteGoOutlined";
import {
  CURVE_DEFINITIONS,
  createCurveRow,
  curveRowsToSeries,
  ensureCurveRows,
  parseCurveImportText,
  validateCurveRows,
} from "../../utils/windConfig";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

function triggerCsvDownload(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function formatTick(value) {
  return typeof value === "number"
    ? value.toLocaleString("en-GB", { maximumFractionDigits: 2 })
    : value;
}

export default function CurveEditor({
  title,
  curveDefinition = CURVE_DEFINITIONS.power_curve,
  value,
  onChange,
  helperText,
  disabled = false,
  required = false,
}) {
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const chartAxis = theme.palette.text.secondary;
  const chartGrid = isDark ? theme.palette.divider : "#d9e2f1";
  const chartLine = isDark ? theme.palette.primary.main : "#2563eb";
  const chartTooltipStyle = isDark
    ? {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        color: theme.palette.text.primary,
      }
    : undefined;
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [importError, setImportError] = useState("");
  const [viewMode, setViewMode] = useState("table");

  const minimumRows = Math.max(curveDefinition.minPoints || 2, 2);
  const rows = useMemo(() => ensureCurveRows(value, minimumRows), [minimumRows, value]);
  const validation = useMemo(
    () => validateCurveRows(rows, curveDefinition),
    [curveDefinition, rows]
  );
  const previewSeries = useMemo(
    () => curveRowsToSeries(rows, curveDefinition),
    [curveDefinition, rows]
  );

  const commitRows = (nextRows) => {
    onChange(ensureCurveRows(nextRows, minimumRows));
  };

  const resizeRows = (requestedCount) => {
    const safeCount = Math.max(minimumRows, Number(requestedCount) || minimumRows);
    const nextRows = rows.slice(0, safeCount).map((row) => createCurveRow(row));
    while (nextRows.length < safeCount) {
      nextRows.push(createCurveRow());
    }
    commitRows(nextRows);
  };

  const handleRowChange = (rowId, field, nextValue) => {
    commitRows(
      rows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: nextValue,
            }
          : row
      )
    );
  };

  const handleImportRows = (parsedRows) => {
    setImportError("");
    commitRows(parsedRows);
    setPasteText("");
    setPasteOpen(false);
  };

  const applyImportedText = (text) => {
    const parsed = parseCurveImportText(text);
    if (parsed.error) {
      setImportError(parsed.error);
      return;
    }
    handleImportRows(parsed.rows);
  };

  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      applyImportedText(text);
    } catch (err) {
      setImportError(err?.message || "Failed to read the selected file.");
    } finally {
      event.target.value = "";
    }
  };

  const xHeader = `${curveDefinition.xLabel}${curveDefinition.xUnit ? ` (${curveDefinition.xUnit})` : ""}`;
  const yHeader = `${curveDefinition.yLabel}${curveDefinition.yUnit ? ` (${curveDefinition.yUnit})` : ""}`;
  const hasAnyInput = rows.some((row) => row.x !== "" || row.y !== "");
  const exampleRows =
    curveDefinition.key === CURVE_DEFINITIONS.power_coefficient_curve.key
      ? ["3,0.28", "4,0.37", "5,0.41"]
      : ["3,0", "4,80000", "5,180000"];
  const templateCsv = `${xHeader},${yHeader}\n0,0\n${exampleRows.join("\n")}`;
  const incompleteRowCount = rows.filter(
    (row) => (row.x === "" && row.y !== "") || (row.x !== "" && row.y === "")
  ).length;
  const emptyRowCount = rows.filter((row) => row.x === "" && row.y === "").length;
  const shouldWarnForMissingRows =
    required && rows.length > 0 && (emptyRowCount > 0 || incompleteRowCount > 0);
  const bulkPasteExample = `${xHeader},${yHeader}\n${exampleRows.join("\n")}`;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {title || curveDefinition.label}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Define the number of rows first, then enter the curve values.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              type="number"
              label="Points"
              value={rows.length}
              onChange={(event) => resizeRows(event.target.value)}
              inputProps={{ min: minimumRows, step: 1 }}
              disabled={disabled}
              sx={{ width: 110 }}
            />
            <Button
              size="small"
              startIcon={<ContentPasteGoOutlinedIcon />}
              onClick={() => setPasteOpen((open) => !open)}
              disabled={disabled}
            >
              Bulk paste
            </Button>
            <Button
              size="small"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
            >
              Import CSV / TSV
            </Button>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => triggerCsvDownload(`${curveDefinition.key}_template.csv`, templateCsv)}
            >
              Download template
            </Button>
          </Stack>
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
          onChange={handleFileImport}
        />

        <Collapse in={pasteOpen}>
          <Stack spacing={1.25}>
            <TextField
              multiline
              minRows={4}
              label="Paste curve data"
              placeholder={`One point per line using two columns.\n${bulkPasteExample}`}
              value={pasteText}
              onChange={(event) => setPasteText(event.target.value)}
              disabled={disabled}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Expected format: one point per line with exactly two columns:
              {" "}
              <Box component="span" sx={{ fontFamily: "Consolas, Menlo, Monaco, monospace" }}>
                {`${curveDefinition.xLabel.toLowerCase()}, ${curveDefinition.yLabel.toLowerCase()}`}
              </Box>
              .
              Accepted separators: comma, semicolon, tab, or spaces. An optional header row is
              allowed.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={() => applyImportedText(pasteText)}
                disabled={disabled || !pasteText.trim()}
              >
                Apply pasted data
              </Button>
              <Button size="small" onClick={() => setPasteText("")} disabled={disabled}>
                Clear
              </Button>
            </Stack>
          </Stack>
        </Collapse>

        {helperText && (
          <Stack spacing={0.6}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {helperText}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Bulk paste format: one point per line with two columns, for example
              {" "}
              <Box component="span" sx={{ fontFamily: "Consolas, Menlo, Monaco, monospace" }}>
                {exampleRows[0]}
              </Box>
              {" "}or{" "}
              <Box component="span" sx={{ fontFamily: "Consolas, Menlo, Monaco, monospace" }}>
                {exampleRows[1]}
              </Box>
              . Tab-separated values also work, and the header row is optional.
            </Typography>
          </Stack>
        )}

        {importError && <Alert severity="error">{importError}</Alert>}
        {(required || hasAnyInput ? validation.formErrors : []).map((error) => (
          <Alert key={error} severity="warning">
            {error}
          </Alert>
        ))}
        {shouldWarnForMissingRows && (
          <Alert severity="warning">
            Fill all {rows.length} visible rows for {title || curveDefinition.label.toLowerCase()}.
          </Alert>
        )}

        <Divider />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Curve preview
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_event, nextMode) => {
              if (nextMode) setViewMode(nextMode);
            }}
          >
            <ToggleButton value="table">Table</ToggleButton>
            <ToggleButton value="graph">Graph</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {viewMode === "table" ? (
          <TableContainer variant="outlined" sx={{ borderRadius: 2, maxHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 56, fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>{xHeader}</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 170 }}>{yHeader}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const rowErrors = validation.rowErrors[row.id] || {};
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ color: "text.secondary" }}>{index + 1}</TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={row.x}
                          onChange={(event) => handleRowChange(row.id, "x", event.target.value)}
                          error={Boolean(rowErrors.x)}
                          inputProps={{ step: "any" }}
                          disabled={disabled}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={row.y}
                          onChange={(event) => handleRowChange(row.id, "y", event.target.value)}
                          error={Boolean(rowErrors.y)}
                          inputProps={{ step: "any", min: curveDefinition.minY ?? undefined }}
                          disabled={disabled}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box>
            {previewSeries.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Add at least two valid points to preview the curve.
              </Typography>
            ) : (
              <Box sx={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={previewSeries}
                    margin={{ top: 12, right: 24, left: 12, bottom: 12 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                    <XAxis
                      dataKey="x"
                      tickFormatter={formatTick}
                      tick={{ fill: chartAxis, fontSize: 12 }}
                      stroke={chartAxis}
                      label={{
                        value: xHeader,
                        position: "insideBottom",
                        offset: -4,
                        fontSize: 12,
                        fill: chartAxis,
                      }}
                    />
                    <YAxis
                      width={90}
                      tickFormatter={formatTick}
                      tick={{ fill: chartAxis, fontSize: 12 }}
                      stroke={chartAxis}
                      label={{
                        value: yHeader,
                        angle: -90,
                        position: "insideLeft",
                        fontSize: 12,
                        fill: chartAxis,
                      }}
                    />
                    <RechartsTooltip
                      formatter={(pointValue) => formatTick(pointValue)}
                      labelFormatter={(label) => `${curveDefinition.xLabel}: ${formatTick(label)}`}
                      contentStyle={chartTooltipStyle}
                      labelStyle={isDark ? { color: theme.palette.text.secondary } : undefined}
                      itemStyle={isDark ? { color: theme.palette.text.primary } : undefined}
                    />
                    <Line
                      type="monotone"
                      dataKey="y"
                      stroke={chartLine}
                      strokeWidth={2.5}
                      dot={{ r: 2, fill: chartLine, stroke: chartLine }}
                      activeDot={{ r: 4, fill: chartLine, stroke: chartLine }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
